import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';
function TestSecondAPI() {
  const { authenticatedFetch, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchDataFromSecondAPI = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await authenticatedFetch(`${config.BACKEND_API_ADDRESS}/protected`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Data from second API:', data);
      setResult(data);
    } catch (error) {
      console.error('Error fetching from second API:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const testProjectEndpoint = async (projectId) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/projects/${projectId}/data`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Project data from second API:', data);
      setResult(data);
    } catch (error) {
      console.error('Error fetching project data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const testAdminEndpoint = async (projectId) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/projects/${projectId}/admin`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Admin data from second API:', data);
      setResult(data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const createData = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await authenticatedFetch(`${config.BACKEND_API_ADDRESS}/data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: 'Test Data',
          value: 'This is a test',
          timestamp: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Created data:', data);
      setResult(data);
    } catch (error) {
      console.error('Error creating data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Test Second API Authentication
        </h1>
        <p className="text-gray-600 mb-8">
          Test various endpoints on the second API with authentication
        </p>

        {/* User Info */}
        {user && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-2">Current User</h2>
            <p className="text-sm text-gray-600">Email: {user.email}</p>
            <p className="text-sm text-gray-600">User ID: {user.id}</p>
            {user.projects && user.projects.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700">Projects:</p>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  {user.projects.map((project) => (
                    <li key={project.id}>
                      {project.name} ({project.user_role})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Test Buttons */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Test Endpoints</h2>
          
          <div className="space-y-3">
            <button
              onClick={fetchDataFromSecondAPI}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded transition"
            >
              {loading ? 'Loading...' : 'Test Protected Endpoint'}
            </button>

            <button
              onClick={createData}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded transition"
            >
              {loading ? 'Loading...' : 'Test POST Endpoint (Create Data)'}
            </button>

            {user?.projects && user.projects.length > 0 && (
              <>
                <button
                  onClick={() => testProjectEndpoint(user.projects[0].id)}
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded transition"
                >
                  {loading ? 'Loading...' : `Test Project Endpoint (${user.projects[0].name})`}
                </button>

                <button
                  onClick={() => testAdminEndpoint(user.projects[0].id)}
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded transition"
                >
                  {loading ? 'Loading...' : `Test Admin Endpoint (${user.projects[0].name})`}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Results */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-red-900 mb-2">Error</h2>
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {result && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-green-900 mb-2">Success</h2>
            <pre className="bg-white p-4 rounded border border-green-300 overflow-x-auto text-sm">
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-2">Instructions</h2>
          <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
            <li>Make sure your auth API is running on port 8000</li>
            <li>Make sure your second API is running on port 8001</li>
            <li>Click the buttons above to test different endpoints</li>
            <li>Check the browser console for detailed logs</li>
            <li>The admin endpoint will fail unless you have admin role</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default TestSecondAPI;